import React, { memo, useCallback, useContext, useEffect, useRef, useState } from 'react';
import WellService from 'business/services/WellService';
import TextInput from 'components/customComponents/Experiment/ImagingSchedule/TextInput';
import Table from './scoreTable';
import { RMCheckBoxY } from 'components/customComponents/Controls/RMCheckbox';
import { getNewId } from 'utils/Global';
import { EntityStateDTO, ScoresGroupsDTO } from 'business/models/Models';
import { generalSetupContext } from '../SetupDashboard';
import { RMOKButton } from 'components/customComponents/Layers/InputGroups';
import cloneDeep from 'lodash/cloneDeep';
import _ from 'lodash';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import { ModalDetails } from 'business/models/CustomModels';
import { RMModal } from 'components/modal';

const AUTO_SCORE_DISABLED_GROUP_NAMES = [
  'protein diffusion rate - single component',
  'protein diffusion rate - double component',
  'lipid diffusion rate',
  'protein mobile fraction - single component',
  'protein mobile fraction - double component',
  'lipid mobile fraction',
  'protein recovery time - single component',
  'protein recovery time - double component',
  'lipid recovery time',
  'uv auto score',
  'shg auto score',
  'formulatrix score group',
  'marco',
  'marco uv (beta)',
  'marco vis-uv (beta)',
  'sherlock',
  'sherlock2',
  'sherlock3',
  'sherlock4',
  'sherlock5',
  'uv-tpef auto score',
  'sherlock3.1',
  'ultraviolet auto score',
];

function shouldDisableAutoScore(name?: string) {
  return AUTO_SCORE_DISABLED_GROUP_NAMES.includes(name?.trim().toLowerCase() ?? '');
}

interface Props {
  setShowModal: (showModal: boolean) => void;
}

export const ScoreGroupSettings = memo((props: Props) => {
  const { setShowModal } = props;
  const { scoresGroupsList, setScoresGroupsList, scoresList, setScoresList } =
    useContext(generalSetupContext);
  const [tempScoreGroups, setTempScoreGroups] = useState<ScoresGroupsDTO[]>(
    cloneDeep([...scoresGroupsList]),
  );
  const [tempScoresList, setTempScoresList] = useState(scoresList);
  const [values, setValues] = useState<
    {
      name: JSX.Element;
      autoScore: JSX.Element;
      unit: JSX.Element;
      inherits: JSX.Element;
    }[]
  >([]);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(-1);
  const [modalDetails, setModalDetails] = useState<ModalDetails>({
    showModal: false,
    setShowModal: () => {},
    onOk: () => {},
    body: null,
    heading: '',
    isOkOnly: false,
    okButtonText: undefined,
    showButtons: true,
    onCancel: () => {},
  });
  const tableBottomRef = useRef(null);

  const setupScoreTable = useCallback(() => {
    const scoreGroups = tempScoreGroups.filter(
      (scoreGroup) => scoreGroup.entityState !== EntityStateDTO.Deleted,
    );

    const tableData = scoreGroups.map((scoreGroup, index) => {
      const entry = {
        name: (
          <TextInput
            type="text"
            value={scoreGroup.name}
            id={`scoregroupsettings-input-setupscoretable-${scoreGroup.name}`}
            rowData={scoreGroup}
            onChange={onChangeName}
            index={index}
            disabled={scoreGroup.isAutoScore}
          />
        ),
        autoScore: (
          <RMCheckBoxY
            isChecked={scoreGroup.isAutoScore}
            onChange={() => onCheckAutoScore(index, !scoreGroup.isAutoScore)}
            disabled={shouldDisableAutoScore(scoreGroup.name)}
          />
        ),
        unit: (
          <TextInput
            type="text"
            value={scoreGroup.unit}
            id={`scoregroupsettings-input-setupscoretable-${scoreGroup.unit}`}
            rowData={scoreGroup}
            onChange={onChangeUnit}
            index={index}
          />
        ),
        inherits: (
          <RMCheckBoxY
            isChecked={scoreGroup.isInherit}
            onChange={() => onCheckInherit(index, !scoreGroup.isInherit)}
          />
        ),
      };
      return entry;
    });
    setValues(tableData);
  }, [tempScoreGroups]);

  useEffect(() => {
    setupScoreTable();
  }, [tempScoreGroups]);

  const tableColumns = [
    {
      Header: 'Score Group',
      accessor: 'name',
    },
    {
      Header: 'Auto Score',
      accessor: 'autoScore',
    },
    {
      Header: 'Unit',
      accessor: 'unit',
    },
    {
      Header: 'Inherits',
      accessor: 'inherits',
    },
  ];

  const scoreGrouptable = useCallback(() => {
    if (values !== null) {
      return (
        <Table
          columns={tableColumns}
          data={values}
          onRowClick={onSelectRow}
          selectedRowIndex={selectedRowIndex}
          bottomRef={tableBottomRef}
        />
      );
    }
    return null;
  }, [values, selectedRowIndex]);

  const isNameDuplicate = useCallback(
    (index: number, value: string) => {
      const isDuplicateValue = tempScoreGroups.some(
        (item, itemIndex) =>
          item.name.trim().toLowerCase() === value.trim().toLowerCase() &&
          itemIndex !== index,
      );
      if (isDuplicateValue) {
        alert('Please provide a unique name.');
        return true;
      }
      return false;
    },
    [tempScoreGroups],
  );

  const onChangeName = useCallback(
    ({ value, index }) => {
      if (value.length === 0) {
        alert('Please provide a unique name.');
        return;
      }
      isNameDuplicate(index, value);
      const tempList = [...tempScoreGroups];
      tempList[index].name = value;
      if (tempList[index].entityState !== EntityStateDTO.New) {
        tempList[index].entityState = EntityStateDTO.Modified;
      }

      setTempScoreGroups(tempList);
    },
    [tempScoreGroups],
  );

  const onChangeUnit = useCallback(
    ({ value, index }) => {
      const tempList = [...tempScoreGroups];
      tempList[index].unit = value;
      if (tempList[index].entityState !== EntityStateDTO.New) {
        tempList[index].entityState = EntityStateDTO.Modified;
      }
      setTempScoreGroups(tempList);
    },
    [tempScoreGroups],
  );

  const onCheckAutoScore = useCallback(
    (index: number, value: boolean) => {
      const tempList = [...tempScoreGroups];
      tempList[index].isAutoScore = value;
      if (tempList[index].entityState !== EntityStateDTO.New) {
        tempList[index].entityState = EntityStateDTO.Modified;
      }
      const scoresList = [...tempScoresList];
      scoresList.forEach((score) => {
        if (score.wellDropScoreGroupId === tempList[index].scoreGroupsId) {
          score.isAutoScore = value;
          if (score.entityState !== EntityStateDTO.New) {
            score.entityState = EntityStateDTO.Modified;
          }
        }
      });
      setTempScoreGroups(tempList);
      setTempScoresList(scoresList);
    },
    [tempScoreGroups, tempScoresList],
  );

  const onCheckInherit = useCallback(
    (index: number, value: boolean) => {
      const tempList = [...tempScoreGroups];
      tempList[index].isInherit = value;
      if (tempList[index].entityState !== EntityStateDTO.New) {
        tempList[index].entityState = EntityStateDTO.Modified;
      }
      setTempScoreGroups(tempList);
    },
    [tempScoreGroups],
  );

  const addNewScoreGroup = useCallback(() => {
    const maximumOrderNumber = Math.max(
      ...tempScoreGroups.map((item) => item.orderNumber),
    );
    const newItem = new ScoresGroupsDTO({
      isAutoScore: false,
      isInherit: false,
      name: '',
      scoreGroupsId: getNewId(),
      orderNumber: maximumOrderNumber + 1,
      hit: false,
      unit: '',
      entityState: EntityStateDTO.New,
    });

    if (isNameDuplicate(tempScoreGroups.length, newItem.name)) {
      return;
    }
    const tempList = tempScoreGroups.concat(newItem);
    setSelectedRowIndex(tempScoreGroups.length);
    setTempScoreGroups(tempList);
    tableBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [tempScoreGroups]);

  const deleteScoreGroup = useCallback(async () => {
    if (selectedRowIndex === -1) {
      return;
    }

    const wellservice = new WellService();
    if (tempScoreGroups[selectedRowIndex].scoreGroupsId > 0) {
      const isInUse = await wellservice.isWellScoreGroupInUse(
        tempScoreGroups[selectedRowIndex],
      );
      if (isInUse) {
        setModalDetails({
          showModal: true,
          onOk: () => {
            setModalDetails({ ...modalDetails, showModal: false });
          },
          setShowModal: () => {
            setModalDetails({ ...modalDetails, showModal: false });
          },
          body: (
            <div>
              <label>Score group is in use.</label>
            </div>
          ),
          heading: 'Error',
          isOkOnly: true,
          okButtonText: 'Ok',
          showButtons: true,
          onCancel: () => {},
        });
        return;
      }
    }
    let tempList = [...tempScoreGroups];
    tempList = tempList.filter((scoreGroup, index) => index !== selectedRowIndex);

    setTempScoreGroups(tempList);
  }, [selectedRowIndex]);

  const onSelectRow = useCallback((row: { index: number }) => {
    setSelectedRowIndex(row.index);
  }, []);

  const onClickSave = useCallback(() => {
    if (checkDuplicateNames() === true || checkEmptyNames() === true) {
      alert('Please provide a unique name.');
      return;
    }
    setShowModal(false);
    setScoresGroupsList(tempScoreGroups);
    setScoresList(tempScoresList);
  }, [tempScoreGroups]);

  const onClickCancel = useCallback(() => {
    setShowModal(false);
    setTempScoreGroups(tempScoreGroups);
  }, [tempScoreGroups]);

  const checkEmptyNames = useCallback(() => {
    return tempScoreGroups.some((scoreGroup) => scoreGroup.name.length === 0);
  }, [tempScoreGroups]);

  const checkDuplicateNames = useCallback(() => {
    const groupNames = tempScoreGroups.map((scoreGroup) => {
      return scoreGroup.name.trim().toLowerCase();
    });
    return _.uniq(groupNames).length !== groupNames.length;
  }, [tempScoreGroups]);

  const handleEscapeKey = useCallback((event: KeyboardEvent) => {
    if (event.code === 'Escape') {
      onClickCancel();
    }
  }, []);

  // Cancelling the changes on pressing escape button.
  useEffect(() => {
    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, []);

  return (
    <>
      <div
        id="scoregroupsettings-table"
        style={{ height: '300px', maxHeight: '300px', overflowY: 'scroll' }}
      >
        {scoreGrouptable()}
      </div>
      <div
        id="scoregroupsettings-add-button"
        className="p-1 float-left cursor-pointer pt-2"
        onClick={(e) => addNewScoreGroup()}
      >
        <RMSvgIcon
          id="scoregroup-svg-add-icon"
          name="Add"
          width={'28px'}
          height={'28px'}
          fill="#474747"
          subfolder="Setup"
        ></RMSvgIcon>
      </div>
      <div
        id="scoregroupsettings-delete-button"
        className="p-1 float-left cursor-pointer pt-2"
        onClick={(e) => deleteScoreGroup()}
      >
        <RMSvgIcon
          id="scoregroupsettings-svg-delete-icon"
          name="Remove"
          width={'28px'}
          height={'28px'}
          opacity={selectedRowIndex === -1 ? 0.5 : 1}
          fill="#474747"
          subfolder="Setup"
        ></RMSvgIcon>
      </div>
      <div
        id="scoregroupsettings-cancel-button"
        className="p-1 float-right cursor-pointer"
      >
        <RMOKButton onClick={onClickCancel} text={'Cancel'}></RMOKButton>
      </div>
      <div id="scoregroupsettings-save-button" className="p-1 float-right">
        <RMOKButton onClick={onClickSave} text={'Save'}></RMOKButton>
      </div>
      <RMModal
        id="scoregroupsettings-confirmation-modal"
        showModal={modalDetails.showModal}
        setShowModal={modalDetails.setShowModal}
        body={modalDetails.body}
        heading={modalDetails.heading}
        showButtons={modalDetails.showButtons}
        onOk={() => modalDetails.onOk()}
        onCancel={() => modalDetails.onCancel()}
        isOkOnly={modalDetails.isOkOnly}
        okButtonText={modalDetails.okButtonText}
      />
    </>
  );
});
